import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react';

type ToastType = 'success' | 'warning' | 'error';

interface Toast {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type: ToastType) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

const ICON_MAP: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={28} className="text-green-400" />,
  warning: <AlertTriangle size={28} className="text-yellow-400" />,
  error: <XCircle size={28} className="text-red-400" />,
};

const BORDER_MAP: Record<ToastType, string> = {
  success: 'border-green-500/30',
  warning: 'border-yellow-500/30',
  error: 'border-red-500/30',
};

const BG_MAP: Record<ToastType, string> = {
  success: 'bg-green-500/10',
  warning: 'bg-yellow-500/10',
  error: 'bg-red-500/10',
};

let toastId = 0;

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = React.useState<Toast[]>([]);
  const timersRef = React.useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
    };
  }, []);

  const showToast = React.useCallback((message: string, type: ToastType) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, message, type }]);
    const timer = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      timersRef.current.delete(id);
    }, 3000);
    timersRef.current.set(id, timer);
  }, []);

  const dismiss = React.useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {toasts.map((toast) => (
        <div key={toast.id} className="fixed inset-0 z-[100] flex items-center justify-center" onClick={() => dismiss(toast.id)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className={`relative z-10 flex items-start gap-4 rounded-2xl border ${BORDER_MAP[toast.type]} ${BG_MAP[toast.type]} backdrop-blur-xl px-6 py-5 max-w-md shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            {ICON_MAP[toast.type]}
            <p className="text-sm text-white pt-0.5">{toast.message}</p>
            <button onClick={() => dismiss(toast.id)} className="text-slate-400 hover:text-white transition-colors ml-2">
              <X size={16} />
            </button>
          </div>
        </div>
      ))}
    </ToastContext.Provider>
  );
};
